import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X, TrendingUp } from "lucide-react";
import { useShop } from "@/context/ShopContext";
import { PRODUCTS, CATEGORY_LABEL } from "@/data/products";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const POPULAR = ["Puff Print", "Embroidery", "Rhinestone", "Denim", "Hoodie", "AR6215MT"];

export const SearchOverlay = () => {
  const { searchOpen, setSearchOpen } = useShop();
  const [q, setQ] = useState("");
  const navigate = useNavigate();

  React.useEffect(() => { if (!searchOpen) setQ(""); }, [searchOpen]);

  const results = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return [];
    return PRODUCTS.filter((p) =>
      `${p.styleCode} ${p.name} ${p.treatment} ${p.graphic || ""} ${p.gender} ${p.category}`.toLowerCase().includes(term)
    ).slice(0, 7);
  }, [q]);

  const go = (id) => { setSearchOpen(false); navigate(`/product/${id}`); };

  return (
    <Dialog open={searchOpen} onOpenChange={setSearchOpen}>
      <DialogContent data-testid="search-overlay" className="p-0 gap-0 max-w-2xl top-[12%] translate-y-0 bg-white overflow-hidden">
        <DialogTitle className="sr-only">Search the collection</DialogTitle>
        <DialogDescription className="sr-only">Type to search the AR / American Rag line by style, graphic or treatment.</DialogDescription>
        <div className="flex items-center gap-3 px-5 border-b">
          <Search className="w-5 h-5 text-muted-foreground" />
          <input
            autoFocus
            data-testid="search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by style code, graphic or treatment…"
            className="flex-1 py-5 text-base outline-none bg-transparent"
          />
          <button onClick={() => setSearchOpen(false)} aria-label="Close"><X className="w-5 h-5" /></button>
        </div>

        <div className="max-h-[55vh] overflow-y-auto">
          {!q.trim() && (
            <div className="p-5">
              <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground mb-3 flex items-center gap-2">
                <TrendingUp className="w-3.5 h-3.5" /> Popular searches
              </p>
              <div className="flex flex-wrap gap-2">
                {POPULAR.map((t) => (
                  <button key={t} onClick={() => setQ(t)} className="px-3 py-1.5 bg-sand text-sm hover:bg-denim hover:text-white transition-colors rounded-full">
                    {t}
                  </button>
                ))}
              </div>
            </div>
          )}

          {q.trim() && results.length === 0 && (
            <p className="p-8 text-center text-muted-foreground text-sm">No results for "{q}".</p>
          )}

          {results.length > 0 && (
            <div className="p-2">
              {results.map((p) => (
                <button
                  key={p.id}
                  data-testid={`search-result-${p.id}`}
                  onClick={() => go(p.id)}
                  className="w-full flex items-center gap-4 p-3 hover:bg-sand transition-colors text-left"
                >
                  <img src={p.image} alt={p.name} className="w-14 h-[72px] object-cover object-top bg-sand" />
                  <div className="flex-1 min-w-0">
                    <p className="font-mono text-[10px] uppercase tracking-widest text-denim">{p.styleCode}</p>
                    <h4 className="text-sm font-heading font-semibold truncate">{p.name}</h4>
                    <p className="text-xs text-muted-foreground truncate">{CATEGORY_LABEL[p.category]}</p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
